import { useEffect, useState } from 'react';
import { TextStyled } from '../../component-library';
import { Icon, rebrandedIbisVisionLogo, Text, Flex } from '../../2022-component-library';
import { VideoChat } from '../../components';
import { useDistanceDetector } from '../../helpers/facial-recognition-hooks';
import { usePostMessage, useReceiveMessage } from '../../context/messageContext';
import { calibrationConstants } from '../../helpers/calibration-constants';
import { useCalibration } from '../../context/calibrationContext';
import { useBreakpoints } from '../../helpers/useBreakpoints';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

const shine = keyframes`
0% {
   background-position: right;
  }
  `;

const ShineOverlay = styled(Flex)`
  height: 100%;
  width: 100%;
  margin: 5px;
  position: absolute;
  background: linear-gradient(90deg, #0000 33%, rgba(255, 255, 255, 0.3) 50%, #0000 66%);
  background-size: 300% 100%;
  animation: ${shine} 1s infinite;
  z-index: 10;
`;

const messageStyles = {
  position: 'absolute',
  flexDirection: 'column',
  width: '100vw',
  height: '100vh',
  backgroundColor: 'white',
  zIndex: '100',
  justifyContent: 'center',
  alignItems: 'center',
};

const PupillaryDistance = () => {
  const { breakpoints } = useBreakpoints();
  const [startFaceRecognition, setStartFaceRecognition] = useState(false);
  const { movementNotification, start } = useDistanceDetector(startFaceRecognition);
  const [detectDistance, setDetectDistance] = useState(false);
  const postMessage = usePostMessage();
  const messageFromOptician = useReceiveMessage();
  const [isMoving, setIsMoving] = useState(false);
  const [movementCounter, setMovementCounter] = useState(3);
  const [totalChanges, setTotalChanges] = useState(0);
  const {
    calibrationData: { perceivedFocalLength },
  } = useCalibration();

  useEffect(() => {
    if (startFaceRecognition === false) {
      setIsMoving(false);
      setDetectDistance(false);
      return;
    }

    if (movementNotification) {
      setMovementCounter(movementNotification.counter);

      if (movementNotification.patientIsMoving === true) {
        setIsMoving(true);
      } else {
        if (isMoving === true) {
          setDetectDistance(false);
          setStartFaceRecognition(false);
        }
        setIsMoving(false);
      }

      postMessage({
        type: 'patientMovementDetected',
        content: movementNotification,
      });
    }
  }, [movementNotification, startFaceRecognition]);

  useEffect(() => {
    setTotalChanges(0);
    if (detectDistance === true && startFaceRecognition === true) {
      start(0, true, perceivedFocalLength ? perceivedFocalLength : calibrationConstants.perceivedFocalLength);
    }
  }, [detectDistance, startFaceRecognition, perceivedFocalLength]);

  useEffect(() => {
    switch (messageFromOptician.type) {
      case 'startPupillaryDistanceDetection':
        setDetectDistance(true);
        setStartFaceRecognition(true);
        break;
      case 'stopPupillaryDistanceDetection':
        setDetectDistance(false);
        setStartFaceRecognition(false);
        break;
      default:
        break;
    }
  }, [messageFromOptician]);

  useEffect(() => {
    setTotalChanges((prev) => prev + 1);
  }, [movementNotification]);

  useEffect(() => {
    if (totalChanges >= 3 && Number(movementCounter) > 3) {
      setTotalChanges(3);
    }
  }, [totalChanges, movementCounter]);

  return (
    <>
      <VideoChat stacked customStyle={{ videoBox: { position: 'fixed' } }} />
      <Flex {...messageStyles} fontSize={breakpoints('1.5rem', '2rem', '3rem')}>
        {detectDistance ? (
          <>
            <Text textAlign='center' fontSize='48px'>
              Detecting your distance
            </Text>
            <br />
            <Text p={2} textAlign='center'>
              Please stay still...
            </Text>
            <progress value={totalChanges - 1} max={6} className='custom-progress' style={{ color: 'green' }} />
            <ShineOverlay />
          </>
        ) : (
          <TextStyled textAlign='center' fontSize='48px'>
            {movementNotification?.patientDistance ? (
              <>
                Your distance has been detected!
                <br />
                Please wait for the clinician's instructions
              </>
            ) : (
              <>
                Your distance will be detected
                <br />
                Please wait for the clinician's instructions
              </>
            )}
          </TextStyled>
        )}
      </Flex>
    </>
  );
};

export default PupillaryDistance;
